import React from 'react'
import { useState } from 'react'
import { addOrRemove } from '../../../../../util/util'
import { PublisherPlatformsMode } from './publisher-platforms.types'
import {
  PlacementPositions,
  PlacementPositionKey,
  PublisherPlatform,
  publisherPlatforms,
  sendablePlacementPositions,
} from '@marketing-milk/interfaces'
import { isEmpty, omit } from 'lodash'

export type HandlePlatformModeChange = (mode: PublisherPlatformsMode) => void
export type HandlePlatformSelection = (platform: PublisherPlatform) => void
export type HandlePositionSelection = (key: PlacementPositionKey, position: string) => void

export function usePublisherPlatforms(
  platforms?: PublisherPlatform[],
  positions?: PlacementPositions
) {
  const [selectedPublisherPlatforms, setSelectedPublisherPlatforms] = useState(
    platforms?.length ? platforms : undefined
  )
  const [selectedPositions, setSelectedPositions] = useState(positions)

  const handlePlatformModeChange = (mode: PublisherPlatformsMode) => {
    if (mode === 'automatic') {
      setSelectedPublisherPlatforms(undefined)
    } else {
      setSelectedPublisherPlatforms([...publisherPlatforms])
    }
    // positions only mean something inside a manual platform selection
    setSelectedPositions(undefined)
  }

  const handlePlatformSelection = (platform: PublisherPlatform) => {
    // Return early if user is trying to unselect the last checkbox. At least one must be selected.
    if (selectedPublisherPlatforms?.length === 1 && selectedPublisherPlatforms[0] === platform)
      return
    setSelectedPublisherPlatforms(existing =>
      existing ? addOrRemove(existing, platform) : existing
    )
  }

  const handlePositionSelection = (key: PlacementPositionKey, position: string) => {
    const all: readonly string[] = sendablePlacementPositions[key]
    // an absent key means every position, so that is what an untouched platform starts from
    const current = (selectedPositions?.[key] ?? [...all]) as string[]
    const next = addOrRemove(current, position)
    // Same rule as platforms: never let the last one be unticked. Zero positions would serialize
    // identically to all of them, so a column of empty boxes would deliver everywhere.
    if (next.length === 0) return
    setSelectedPositions(existing => {
      if (next.length < all.length) return { ...existing, [key]: next } as PlacementPositions
      // back to every position on this platform, so the key goes away — and with no keys left
      // there is nothing to say, which is the same thing an untouched campaign says
      const remaining = omit(existing, key)
      return isEmpty(remaining) ? undefined : remaining
    })
  }

  return {
    selectedPublisherPlatforms,
    selectedPositions,
    handlePlatformModeChange,
    handlePlatformSelection,
    handlePositionSelection,
  }
}
